import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Page, PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { naira } from "../lib/format";
import { Sunrise, RefreshCw, TrendingUp, Coins, Boxes, Inbox, AlertTriangle, Send } from "lucide-react";

const CARD = { background: "#FFFDF9", borderColor: "#E9DED0" };

const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

function Kpi({ label, value, sub, icon: Icon, color = "#3B2722", testid }) {
  return (
    <div className="rounded-2xl border px-5 py-4 flex items-center justify-between" style={CARD} data-testid={testid}>
      <div>
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#765A4A" }}>{label}</div>
        <div className="font-brand font-bold text-3xl mt-1" style={{ color }}>{value}</div>
        {sub && <div className="text-xs mt-0.5" style={{ color: "#a58f7c" }}>{sub}</div>}
      </div>
      <Icon size={28} strokeWidth={1.3} style={{ color: "#C49A4A" }} />
    </div>
  );
}

export default function OwnerDigest() {
  const [day, setDay] = useState(yesterday());
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = (dd = day) => {
    setLoading(true);
    api.get("/digest/daily", { params: { day: dd } })
      .then(setD).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(day); /* eslint-disable-next-line */ }, [day]);

  const push = async () => {
    setSending(true);
    try {
      const r = await api.post("/digest/run", { day, force: true });
      toast.success(`Digest sent to ${r.notified} recipient(s)`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  return (
    <Page>
      <PageHeader title="Daily Owner Digest" subtitle="One short morning summary: sales, margin, cash, stock and what needs attention"
        actions={<>
          <Button variant="outline" onClick={() => load(day)} data-testid="digest-refresh"><RefreshCw size={15} className="mr-1" /> Refresh</Button>
          <Button onClick={push} disabled={sending} data-testid="digest-send" className="text-white" style={{ background: "#8B1E1E" }}>
            <Send size={15} className="mr-1" /> {sending ? "Sending…" : "Send now"}
          </Button>
        </>} />

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="space-y-1.5">
          <Label className="text-xs">Business day</Label>
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="digest-day" className="w-48" />
        </div>
        <div className="flex items-center gap-2 text-xs" style={{ color: "#a58f7c" }}>
          <Sunrise size={15} style={{ color: "#C49A4A" }} /> Delivered in-app every morning at 7:00 (Lagos) for the previous day.
        </div>
      </div>

      {loading && <div className="h-28 rounded-2xl animate-pulse" style={{ background: "#F3EAD6" }} />}

      {!loading && d && (
        <div className="space-y-5" data-testid="digest-body">
          <div className="rounded-2xl border px-5 py-4" style={CARD}>
            <div className="text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: "#765A4A" }}>Headline</div>
            <div className="text-sm md:text-base font-medium" style={{ color: "#3B2722" }} data-testid="digest-headline">{d.headline}</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <Kpi label="Sales" value={naira(d.sales.revenue)} sub={`${d.sales.orders} orders · avg ${naira(d.sales.avg_ticket)}`} icon={TrendingUp} testid="digest-kpi-sales" />
            <Kpi label="Gross Profit" value={naira(d.margin.gross_profit)} sub={`${d.margin.gross_margin_pct}% margin · COGS ${naira(d.margin.cogs)}`} icon={Coins} color="#4F7A52" testid="digest-kpi-margin" />
            <Kpi label="Cash Variance" value={naira(d.cash.variance)} sub={`${d.cash.shifts_closed} shift(s) closed · ${d.cash.exceptions.length} exception(s)`} icon={Coins} color={Math.abs(d.cash.variance) > 0 ? "#A52222" : "#3B2722"} testid="digest-kpi-cash" />
            <Kpi label="Low Stock" value={d.low_stock.count} sub={`${d.approvals.count} approval(s) pending`} icon={Boxes} color="#B7791F" testid="digest-kpi-stock" />
          </div>

          <div className="rounded-2xl border px-5 py-4" style={CARD} data-testid="digest-attention">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={15} style={{ color: "#B7791F" }} />
              <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#765A4A" }}>Needs attention</span>
            </div>
            {d.needs_attention.length === 0
              ? <p className="text-sm" style={{ color: "#4F7A52" }}>Nothing needs your attention — a clean day.</p>
              : <ul className="space-y-1.5">{d.needs_attention.map((a, i) => (
                  <li key={i} className="text-sm flex gap-2" style={{ color: "#3B2722" }}><span style={{ color: "#8B1E1E" }}>•</span> {a}</li>))}
                </ul>}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="rounded-2xl border p-4" style={CARD} data-testid="digest-cash-exceptions">
              <div className="text-[11px] uppercase tracking-widest font-semibold mb-3" style={{ color: "#765A4A" }}>Till variances</div>
              {d.cash.exceptions.length === 0 ? <p className="text-sm" style={{ color: "#a58f7c" }}>Every till balanced.</p>
                : d.cash.exceptions.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0" style={{ borderColor: "#F0E9DD" }}>
                    <span style={{ color: "#3B2722" }}>{e.till} · {e.cashier}</span>
                    <span className="font-semibold" style={{ color: e.cash_variance < 0 ? "#A52222" : "#4F7A52" }}>{naira(e.cash_variance)}</span>
                  </div>))}
            </div>

            <div className="rounded-2xl border p-4" style={CARD} data-testid="digest-low-stock">
              <div className="text-[11px] uppercase tracking-widest font-semibold mb-3" style={{ color: "#765A4A" }}>Low stock</div>
              {d.low_stock.items.length === 0 ? <p className="text-sm" style={{ color: "#a58f7c" }}>All stock above thresholds.</p>
                : d.low_stock.items.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0" style={{ borderColor: "#F0E9DD" }}>
                    <span className="truncate pr-2" style={{ color: "#3B2722" }}>{s.product_name}<span className="block text-[11px]" style={{ color: "#a58f7c" }}>{s.location_name}</span></span>
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full shrink-0"
                      style={{ background: s.level === "critical" ? "#F7E5E5" : "#FBF0DE", color: s.level === "critical" ? "#A52222" : "#B7791F" }}>{s.level}</span>
                  </div>))}
            </div>

            <div className="rounded-2xl border p-4" style={CARD} data-testid="digest-approvals">
              <div className="flex items-center gap-2 mb-3"><Inbox size={14} style={{ color: "#8B1E1E" }} />
                <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#765A4A" }}>Awaiting approval</span></div>
              {d.approvals.items.length === 0 ? <p className="text-sm" style={{ color: "#a58f7c" }}>Inbox is clear.</p>
                : d.approvals.items.map((a) => (
                  <div key={a.id} className="text-sm py-1.5 border-b last:border-0" style={{ borderColor: "#F0E9DD" }}>
                    <div className="font-medium" style={{ color: "#3B2722" }}>{a.title || a.workflow_type}</div>
                    <div className="text-[11px]" style={{ color: "#a58f7c" }}>{a.requester_name} · awaiting {a.awaiting}</div>
                  </div>))}
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
